<template>
  <v-card outlined class="fill-height d-flex flex-column">
    <v-toolbar flat dense color="primary" dark>
      <v-icon small class="mr-2">fab fa-whatsapp</v-icon>
      <v-toolbar-title class="subtitle-1">
        {{ $tc('conversation', 2) }}
      </v-toolbar-title>
      <v-spacer />
      <v-chip x-small :color="connected ? 'green' : 'grey'" dark>
        {{ connected ? $tc('connected', 1) : $tc('disconnected', 1) }}
      </v-chip>
    </v-toolbar>

    <v-card-text class="pb-0">
      <v-text-field
        v-model="search"
        :label="$t('search')"
        prepend-inner-icon="fa-search"
        outlined
        dense
        clearable
        hide-details
        @input="debouncedLoad"
      />
      <!-- Sin active-class el chip elegido queda transparente y los otros
           grises: se lee al revés, como si los grises fueran los activos. -->
      <v-chip-group
        v-model="filter"
        mandatory
        column
        active-class="primary white--text"
        @change="load"
      >
        <v-chip small value="all">{{ $t('all') }}</v-chip>
        <v-chip small value="unread">{{ $t('unread') }}</v-chip>
        <v-chip small value="mine">{{ $t('assigned_to_me') }}</v-chip>
        <v-chip small value="none">{{ $t('unassigned') }}</v-chip>
      </v-chip-group>
    </v-card-text>

    <v-divider />

    <div class="conversation-scroll flex-grow-1">
      <v-list two-line dense>
        <v-skeleton-loader v-if="loading" type="list-item-two-line@4" />
        <!-- Buscar y no encontrar no es lo mismo que no tener conversaciones:
             con el mismo texto parece que se vació la bandeja. -->
        <v-list-item v-else-if="conversations.length === 0">
          <v-list-item-content>
            <v-list-item-subtitle class="text-center">
              {{
                search
                  ? $t('no_results_for', { search })
                  : $t('no_conversations')
              }}
            </v-list-item-subtitle>
            <div v-if="search" class="text-center mt-2">
              <i-btn
                :title="$t('clear')"
                text
                small
                color="primary"
                @click="clearSearch()"
              >
                {{ $t('clear') }}
              </i-btn>
            </div>
          </v-list-item-content>
        </v-list-item>
        <v-list-item
          v-for="item in conversations"
          :key="`conversation-${item.pk}`"
          :class="{ 'primary lighten-5': isCurrent(item) }"
          @click="$emit('select', item)"
        >
          <v-list-item-content>
            <v-list-item-title class="font-weight-medium">
              {{ item.customer_name }}
              <v-icon
                v-if="!item.customer"
                x-small
                color="orange"
                :title="$t('unregistered_contact')"
              >
                fa-user-plus
              </v-icon>
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ item.last_message || item.contact_phone }}
            </v-list-item-subtitle>
            <v-list-item-subtitle class="caption">
              <v-chip x-small outlined class="mr-1">
                {{ $t(`whatsapp_state_${item.state}`) }}
              </v-chip>
              <span v-if="item.assigned_user_name">
                {{ item.assigned_user_name }}
              </span>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-list-item-action-text>
              {{ shortDate(item.last_message_at) }}
            </v-list-item-action-text>
            <v-badge
              v-if="item.unread_count > 0"
              :content="item.unread_count"
              color="green"
              inline
            />
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </div>
  </v-card>
</template>
<script>
import { mapGetters } from 'vuex'
export default {
  name: 'conversation-list',
  data() {
    return {
      search: '',
      filter: 'all',
      timer: null
    }
  },
  computed: {
    ...mapGetters({
      conversations: 'whatsapp/getConversations',
      current: 'whatsapp/getCurrent',
      loading: 'whatsapp/getLoading',
      connected: 'whatsapp/getConnected',
      lastEvent: 'whatsapp/getLastEvent',
      company: 'company/getCompanyData'
    })
  },
  watch: {
    /**
     * Una conversación que todavía no está en el listado no la puede mover
     * `touchConversation`, que descarta el evento por no encontrarla. Sin
     * esto, el primer mensaje de un contacto nuevo aparece recién al recargar
     * la página. Cuando el evento es de una conversación desconocida se vuelve
     * a pedir el listado, respetando el filtro y la búsqueda vigentes.
     * @param {Object} event - Evento recibido por el socket.
     */
    lastEvent(event) {
      if (!event || !event.conversation || this.loading) {
        return
      }
      const known = this.conversations.some(
        (item) => item.pk === event.conversation
      )
      if (!known) {
        this.load()
      }
    }
  },
  methods: {
    isCurrent(item) {
      return this.current !== null && this.current.pk === item.pk
    },
    shortDate(value) {
      if (!value) {
        return ''
      }
      return this.$moment(value).format('DD/MM HH:mm')
    },
    clearSearch() {
      this.search = ''
      this.load()
    },
    /**
     * Evita una petición por cada tecla mientras se escribe en el buscador.
     */
    debouncedLoad() {
      clearTimeout(this.timer)
      this.timer = setTimeout(() => this.load(), 400)
    },
    async load() {
      this.$store.dispatch('whatsapp/setLoading', true)
      const params = { company_id: this.company.pk }

      if (this.search) {
        params.search = this.search
      }
      if (this.filter === 'unread') {
        params.unread = 'true'
      }
      if (this.filter === 'mine') {
        params.assigned_user = 'me'
      }
      if (this.filter === 'none') {
        params.assigned_user = 'none'
      }

      try {
        const response = await this.$api.whatsapp.list({ opt: { params } })
        this.$store.dispatch(
          'whatsapp/setConversations',
          response.data.results !== undefined
            ? response.data.results
            : response.data
        )
      } finally {
        this.$store.dispatch('whatsapp/setLoading', false)
      }
    }
  },
  mounted() {
    this.load()
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  }
}
</script>
<style scoped>
/* Vuetify trae `flex: 1 1 auto` en `.v-toolbar` y `flex: 1 1 0` en
   `.v-divider`. Dentro de una tarjeta en columna eso los estira: el
   encabezado se comía buena parte del alto y la línea quedaba flotando con
   aire arriba y abajo. */
.v-card > .v-toolbar,
.v-card > .v-divider {
  flex: none;
  margin: 0;
}
/* `min-height: 0` deja que el listado se achique por debajo de su contenido y
   scrollee por dentro. El tope por alto de ventana sólo hace falta en pantalla
   chica, que es donde la fila no lleva alto fijo. */
.conversation-scroll {
  overflow-y: auto;
  min-height: 0;
}
@media (max-width: 959px) {
  .conversation-scroll {
    max-height: calc(100vh - 320px);
  }
}
</style>
